"use client"

import React from "react"
import { motion } from "framer-motion"
import { ArrowRight, CheckCircle2, Mail, MousePointer2, Eye } from "lucide-react"

interface FunnelStageProps {
  label: string
  value: number | string
  percentage: string
  icon: any
  color: string
  delay: number
}

const FunnelStage = ({ label, value, percentage, icon: Icon, color, delay }: FunnelStageProps) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.6, delay }}
    className="flex flex-col items-center flex-1 relative group"
  >
    <div className={`p-4 rounded-2xl bg-slate-900 border border-white/10 shadow-2xl relative mb-4 transition-all group-hover:border-white/20 group-hover:shadow-[0_0_30px_rgba(255,255,255,0.05)]`}>
      <Icon className={`h-6 w-6 mb-2 ${color}`} />
      <span className="text-2xl font-bold text-white tracking-tight">{value}</span>
      <span className="text-[10px] text-slate-500 uppercase tracking-widest block font-bold">{label}</span>
    </div>
    <div className="absolute top-1/2 -right-4 -translate-y-1/2 z-10 hidden lg:block">
      <ArrowRight className="h-5 w-5 text-slate-700" />
    </div>
    <div className="mt-2 py-1 px-3 rounded-full bg-slate-800/50 border border-white/5 text-[10px] font-bold text-slate-300">
      {percentage}
    </div>
  </motion.div>
)

interface AnalysisFunnelProps {
  stats: {
    sent: number
    delivered: number
    opened: number
    clicked: number
    deliveryRate: number
    openRate: number
    clickRate: number
  }
}

const AnalysisFunnel = ({ stats }: AnalysisFunnelProps) => {
  return (
    <div className="relative p-8 rounded-3xl border border-white/10 bg-slate-900/50 backdrop-blur-3xl overflow-hidden">
      {/* Background Decorative Gradient */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500 opacity-30" />
      
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 relative z-10">
        <FunnelStage
          label="Total Sent"
          value={stats.sent.toLocaleString()}
          percentage="100%"
          icon={Mail}
          color="text-indigo-400"
          delay={0.1}
        />
        <FunnelStage
          label="Delivered"
          value={stats.delivered.toLocaleString()}
          percentage={`${stats.deliveryRate}%`}
          icon={CheckCircle2}
          color="text-blue-400"
          delay={0.2}
        />
        <FunnelStage
          label="Opened"
          value={stats.opened.toLocaleString()}
          percentage={`${stats.openRate}%`}
          icon={Eye}
          color="text-purple-400"
          delay={0.3}
        />
        <FunnelStage
          label="Clicked"
          value={stats.clicked.toLocaleString()}
          percentage={`${stats.clickRate}%`}
          icon={MousePointer2}
          color="text-emerald-400"
          delay={0.4}
        />
      </div>

      {/* Connection Lines (Gradients) */}
      <div className="absolute inset-0 top-1/2 -translate-y-1/2 h-[2px] bg-white/5 mx-20 hidden lg:block" />
    </div>
  )
}

export default AnalysisFunnel
